<template>
  <div
    :class="[
      'playlist-container',
      `ra-playlist--${aspectRatio}`,
      { 'ra-playlist--high-contrast-text': highContrastTextEnabled },
    ]"
    :data-refapp-tagging-id="taggingId"
  >
    <template v-if="currentVideo">
      <div
        :id="`ra-playlist-main-slide-${providerId}`"
        :class="`main-slide ra-playlist--ratio-${aspectRatio}`"
      >
        <div v-if="!isCurrentVideoPlaying" class="main-slide__thumbnail">
          <div class="main-slide__image">
            <img :src="currentVideo.largeThumbnailImage?.src" alt="" />
          </div>
          <div class="main-slide__content">
            <p v-if="showVideoNumber" aria-hidden="true" class="main-slide__video-number">
              {{ currentVideo.index }}
            </p>
            <span class="ra-sr-only">{{ a11yLabels.video }} {{ currentVideoIndex + 1 }}</span>
            <h2 v-if="currentVideo.title" class="main-slide__title">{{ currentVideo.title }}</h2>
            <p v-if="currentVideo.description" class="main-slide__description">
              {{ currentVideo.description }}
            </p>
            <a
              v-if="currentVideo.cta?.text"
              class="main-slide__link"
              :class="currentVideo.cta.style"
              :href="currentVideo.cta.url"
              :data-refapp-tagging-target="`video-cta-${currentVideo.index}`"
            >
              {{ currentVideo.cta.text }}
            </a>
            <div
              role="button"
              tabindex="0"
              :aria-label="a11yLabels.accessVideo"
              class="main-slide__play-icon"
              @keydown.space.prevent
              @keyup.space="playVideo"
              @keydown.enter="playVideo"
              @click="playVideo"
            ></div>
          </div>
        </div>
        <refapp-video-provider
          v-if="isCurrentVideoPlaying && currentVideo.internalVideoId"
          v-bind="internalVideoArgs"
        >
          <refapp-video-base></refapp-video-base>
        </refapp-video-provider>
        <youtube-embed
          v-if="isCurrentVideoPlaying && currentVideo.youtubeId"
          :class="`ra-playlist--ratio-${aspectRatio}`"
          :muted="muted"
          :autoplay="autoplay"
          :video-id="currentVideo.youtubeId"
          :title="currentVideo.title"
        />
      </div>

      <div class="playlist">
        <h2 class="playlist__title">{{ titleLabel }}</h2>
        <ul>
          <li
            v-for="(video, index) in items"
            :key="index"
            :class="{ active: index === currentVideoIndex }"
          >
            <div
              role="button"
              tabindex="0"
              :aria-current="String(index === currentVideoIndex)"
              :aria-controls="`ra-playlist-main-slide-${providerId}`"
              class="playlist__thumbnail-container"
              @keydown.space.prevent
              @keyup.space="selectVideo(index)"
              @keydown.enter="selectVideo(index)"
              @click="selectVideo(index)"
            >
              <div class="playlist__thumbnail">
                <img :src="video.smallThumbnailImage.src" alt="" />
                <p v-if="showVideoNumber" aria-hidden="true" class="playlist__video-number">
                  {{ video.index }}
                </p>
                <span class="ra-sr-only">{{ a11yLabels.video }} {{ index + 1 }}</span>
                <p v-if="index === currentVideoIndex" class="playlist__label">
                  {{ nowShowingLabel }}
                </p>
                <div v-else class="playlist__icon"></div>
              </div>
              <p v-if="video.title" class="playlist__thumbnail-title">{{ video.title }}</p>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import eventBus from '@refapp/eventbus-js';
import { initTagging } from '../lib/tagging';
import RefappVideoProvider from './base/video-provider.vue';
import RefappVideoBase from './base/video.vue';
import YoutubeEmbed from './base/youtube-embed.vue';

export default {
  name: 'refapp-playlist',
  components: {
    RefappVideoProvider,
    RefappVideoBase,
    YoutubeEmbed,
  },
  inject: [
    'index',
    'showVideoNumber',
    'autoplay',
    'muted',
    'aspectRatio',
    'nowShowingLabel',
    'titleLabel',
    'items',
    'taggingId',
    'a11yLabels',
    'providerId',
  ],
  data() {
    return {
      currentVideoIndex: 0,
      isCurrentVideoPlaying: this.autoplay,
    };
  },
  computed: {
    currentVideo() {
      return this.items[this.currentVideoIndex];
    },
    highContrastTextEnabled() {
      return this.currentVideo.highContrastTextEnabled ?? true;
    },
    internalVideoArgs() {
      return {
        autoplay: this.autoplay,
        muted: this.muted,
        controls: true,
        loop: true,
        sources: [this.currentVideo.internalVideoId],
        a11yLabels: this.a11yLabels,
        heading: this.currentVideo.title,
        headingLevel: 2,
        transcript: this.currentVideo.transcript,
        taggingId: this.taggingId,
        highContrastTextEnabled: this.highContrastTextEnabled,
      };
    },
  },
  mounted() {
    initTagging(this.taggingId);
    this.updateTranscript();
  },
  methods: {
    selectVideo(index) {
      this.currentVideoIndex = index;
      this.resetCurrentVideoPlaying();
      this.updateTranscript();
    },
    playVideo() {
      this.isCurrentVideoPlaying = true;

      this.$nextTick(() => {
        this.$el.querySelector('.refapp-video__controls-playback')?.focus();
      });
    },
    resetCurrentVideoPlaying() {
      this.isCurrentVideoPlaying = this.autoplay;
    },
    updateTranscript() {
      this.$nextTick(() => {
        eventBus.emit(`transcript-updated-${this.providerId}`, this.currentVideo?.transcript);
      });
    },
  },
};
</script>
